import React, { Fragment, useCallback, useEffect, useMemo, useRef, useState } from "react";
import moment from "moment";
import { useNavigate } from "react-router-dom";
import { Popover, Transition } from "@headlessui/react";
import { useWorkspaceActivities } from "@components/Activities/useWorkspaceActivities";
import { useActivitiesContext } from "@components/Activities/ActivitiesContext";
import { ActivityTray } from "@components/Activities/ActivityTray";
import type { ActivityAction } from "@components/Activities/views/ActivityItemShell";
import { useActivityTrayStore } from "@components/Activities/useActivityTrayStore";
import Button from "@components/Button";
import { TooltipContainer } from "@scripts/react/components/Tooltip";
import { twClassNames } from "@scripts/react/helpers/twClassNames";
import type { ActivityViewData } from "@components/Activities/types";
import type { ActivityItemOptions } from "@components/Activities/ActivityTray";
import { acknowledgeEvents } from "@scripts/../backendapi/activityApi";

function sortWithUrgentFirst(activities: ActivityViewData[]): ActivityViewData[] {
    const urgent = activities.filter((a) => a.urgent);
    const rest = activities.filter((a) => !a.urgent);
    return [...urgent, ...rest];
}

const CONTROLS_MIN_WIDTH = "8rem";

function RapidActivityItem({
    activity,
    itemOptions
}: {
    activity: ActivityViewData;
    itemOptions?: ActivityItemOptions;
}) {
    const unread = !!itemOptions?.onMarkRead && !activity.read;
    const timestamp = moment(activity.createdAt).fromNow();
    const detailParts = [timestamp, activity.description].filter(Boolean);
    const action = itemOptions?.action;
    const effectiveOnClick = action?.onClick
        ? () => {
              if (unread) itemOptions?.onMarkRead?.();
              action!.onClick();
          }
        : undefined;
    const hasTrailingControls = unread || !!action || !!activity.urgent;

    return (
        <div
            role={effectiveOnClick ? "button" : undefined}
            tabIndex={effectiveOnClick ? 0 : undefined}
            className={twClassNames(
                "group w-[stretch] px-4 py-3",
                effectiveOnClick && "cursor-pointer hover:bg-gray-50"
            )}
            onClick={effectiveOnClick}
            onKeyDown={
                effectiveOnClick
                    ? (e) => {
                          if (e.key === "Enter" || e.key === " ") {
                              e.preventDefault();
                              effectiveOnClick();
                          }
                      }
                    : undefined
            }
        >
            {(activity.contextLabel || hasTrailingControls) && (
                <div className="mb-0.5 flex items-center">
                    {activity.contextLabel && (
                        <p className="min-w-0 flex-1 truncate text-xs font-normal text-gray-500">
                            {activity.contextLabel}
                        </p>
                    )}
                    {!activity.contextLabel && <div className="flex-1" />}
                    {hasTrailingControls && (
                        <div
                            className="relative flex flex-shrink-0 items-center justify-end transition-[min-width]"
                            style={{ minWidth: CONTROLS_MIN_WIDTH }}
                            onClick={(e) => e.stopPropagation()}
                            onKeyDown={(e) => e.stopPropagation()}
                        >
                            {activity.urgent && (
                                <i
                                    className="far fa-circle-exclamation flex-shrink-0 text-base text-yellow-700 transition-opacity duration-150 group-hover:opacity-0"
                                    aria-label="Urgent"
                                />
                            )}
                            <div
                                className={twClassNames(
                                    "absolute inset-y-0 right-0 flex origin-top-right scale-95 items-center justify-end gap-1 opacity-0 transition duration-100 ease-out group-hover:scale-100 group-hover:opacity-100",
                                    "pointer-events-none group-hover:pointer-events-auto"
                                )}
                            >
                                {unread && (
                                    <TooltipContainer
                                        type="titleOnly"
                                        title="Mark as read"
                                        placement="top"
                                        customButton={
                                            <Button
                                                type="secondary"
                                                size="xxsmall"
                                                shape="square"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    itemOptions?.onMarkRead?.();
                                                }}
                                                aria-label="Mark as read"
                                            >
                                                <i className="far fa-check text-sm" />
                                            </Button>
                                        }
                                    />
                                )}
                                {action && (
                                    <Button
                                        type="secondary"
                                        size="xxsmall"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            action.onClick();
                                        }}
                                    >
                                        {action.label}
                                    </Button>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            )}
            <div className="-mb-1 flex h-6 items-center">
                <span
                    className={twClassNames(
                        "flex-shrink-0 rounded-full bg-brand transition-all duration-200",
                        unread ? "mr-2 h-2 w-2 opacity-100" : "mr-0 h-2 w-0 opacity-0"
                    )}
                    aria-hidden
                />
                <span
                    className={twClassNames(
                        "min-w-0 flex-1 truncate text-sm transition-all duration-200",
                        unread ? "font-semibold text-gray-900" : "font-medium text-gray-500"
                    )}
                >
                    {activity.title}
                </span>
            </div>
            {detailParts.length > 0 && (
                <p className="mt-0.5 text-xs text-secondary">
                    {detailParts.map((part, i) => (
                        <React.Fragment key={i}>
                            {i > 0 && <span className="mx-1 text-sm text-gray-900">·</span>}
                            {part}
                        </React.Fragment>
                    ))}
                </p>
            )}
        </div>
    );
}

function renderActivityItem(activity: ActivityViewData, itemOptions?: ActivityItemOptions) {
    return <RapidActivityItem activity={activity} itemOptions={itemOptions} />;
}

export function RapidActivityButton() {
    const { activities: rawActivities, isLoading } = useWorkspaceActivities();
    const { workspaceId } = useActivitiesContext();
    const [localReadIds, setLocalReadIds] = useState<Set<string>>(new Set());
    const navigate = useNavigate();

    const activities = useMemo(() => {
        const merged = rawActivities.map((a) =>
            localReadIds.has(a.id) ? { ...a, read: true } : a
        );
        return sortWithUrgentFirst(merged);
    }, [rawActivities, localReadIds]);

    const unreadCount = activities.filter((a) => !a.read).length;

    const markReadLocally = useCallback((ids: string[]) => {
        setLocalReadIds((prev) => {
            const next = new Set(prev);
            for (const id of ids) next.add(id);
            return next;
        });
    }, []);

    const onMarkRead = useCallback(
        (activity: ActivityViewData) => {
            markReadLocally([activity.id]);
            acknowledgeEvents(workspaceId, [activity.id]).catch(() => {});
        },
        [workspaceId, markReadLocally]
    );

    const onMarkAllRead = useCallback(() => {
        const unreadIds = activities.filter((a) => !a.read).map((a) => a.id);
        if (unreadIds.length === 0) return;
        markReadLocally(unreadIds);
        acknowledgeEvents(workspaceId, unreadIds).catch(() => {});
    }, [activities, workspaceId, markReadLocally]);

    const getAction = useCallback(
        (activity: ActivityViewData): ActivityAction | undefined => {
            if (activity.urgent) return { label: "Buy credits", onClick: () => {} };
            if (!activity.targetUrl) return undefined;
            return {
                label: "View Results",
                onClick: () => {
                    if (activity.openInNewTab) {
                        window.open(activity.targetUrl!, "_blank");
                    } else {
                        navigate(activity.targetUrl!);
                    }
                }
            };
        },
        [navigate]
    );

    const buttonRef = useRef<HTMLButtonElement>(null);
    const storeOpen = useActivityTrayStore((s) => s.isOpen);

    useEffect(() => {
        if (storeOpen) {
            buttonRef.current?.click();
            useActivityTrayStore.getState().close();
        }
    }, [storeOpen]);

    return (
        <Popover className="relative flex items-center">
            {({ open }) => (
                <>
                    <Popover.Button as="div" ref={buttonRef}>
                        <div className="relative inline-flex">
                            <Button type="secondary" size="small" shape="square">
                                <i className="far fa-bell text-sm" />
                            </Button>
                            {unreadCount > 0 && (
                                <span className="absolute -right-1.5 -top-1.5 z-10 flex h-4 min-w-[1rem] items-center justify-center rounded-full bg-brand px-1 text-[10px] font-semibold leading-none text-white">
                                    {unreadCount}
                                </span>
                            )}
                        </div>
                    </Popover.Button>
                    <Popover.Overlay className="fixed inset-0 z-[999]" />
                    <Transition
                        as={Fragment}
                        enter="transition duration-100 ease-out"
                        enterFrom="transform scale-95 opacity-0"
                        enterTo="transform scale-100 opacity-100"
                        leave="transition duration-75 ease-out"
                        leaveFrom="transform scale-100 opacity-100"
                        leaveTo="transform scale-95 opacity-0"
                    >
                        <Popover.Panel className="absolute right-0 top-full z-[1000] mt-2 max-h-[min(70vh,600px)] w-[520px] origin-top overflow-y-auto rounded-lg border border-gray-200 bg-white shadow-lg">
                            <ActivityTray
                                activities={activities}
                                isLoading={isLoading}
                                renderItem={renderActivityItem}
                                title="Activity"
                                onMarkAllRead={onMarkAllRead}
                                onMarkRead={onMarkRead}
                                getAction={getAction}
                            />
                        </Popover.Panel>
                    </Transition>
                </>
            )}
        </Popover>
    );
}
